import {
  CheckCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ShopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Col, Popconfirm, Row, Spin, Typography } from "antd";
import {
  useCreatePickupReceiver,
  useDeletePickupReceiver,
  usePickupReceivers,
  useUpdatePickupReceiver,
} from "hooks/pickup-receiver";
import { usePickupWarehouses } from "hooks/warehouse";
import { useEffect, useState } from "react";
import { notify } from "utils/helperFuncs";
import ReceiverFormModal from "./ReceiverFormModal";

/**
 * Nội dung "Kho nhận hàng" + "Người nhận tại kho" — dùng chung giữa `PickupWarehouseSelect` (modal
 * riêng, bấm [Xác nhận] mới propagate) và nhúng thẳng trong popup đổi PTVC (chọn phát ăn ngay,
 * commit nằm ở nút [Xác nhận] của popup cha).
 */
const PickupWarehousePanel = ({
  sellerID,
  selectedWarehouseID,
  selectedReceiverID,
  onSelectWarehouse,
  onSelectReceiver,
}) => {
  const [receivers, setReceivers] = useState([]);
  const [receiverFormVisible, setReceiverFormVisible] = useState(false);
  const [editingReceiver, setEditingReceiver] = useState(null);
  const [receiversError, setReceiversError] = useState(false);

  const { warehouses: companyOwnedWarehouses, loading: loadingWarehouses } = usePickupWarehouses(sellerID);
  const { handleGetPickupReceivers, loading: loadingReceivers } = usePickupReceivers();
  const { handleCreate, loading: loadingCreate } = useCreatePickupReceiver();
  const { handleUpdate, loading: loadingUpdate } = useUpdatePickupReceiver();
  const { handleDelete, loading: loadingDelete } = useDeletePickupReceiver();

  const loadReceivers = async () => {
    if (!sellerID) return;
    setReceiversError(false);
    try {
      const list = await handleGetPickupReceivers(sellerID);
      setReceivers(list || []);
    } catch {
      setReceiversError(true);
      notify.error({ message: "Không thể tải danh sách người nhận" });
    }
  };

  useEffect(() => {
    if (sellerID) loadReceivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerID]);

  const handleSaveReceiver = async (values) => {
    try {
      if (editingReceiver?.id) {
        const phoneChanged = values.phone !== editingReceiver.phone;
        if (phoneChanged) {
          const existing = receivers.find((r) => r.phone === values.phone && r.id !== editingReceiver.id);
          if (existing) {
            notify.error({ message: "Số điện thoại đã tồn tại, vui lòng nhập số khác" });
            return;
          }
        }
        await handleUpdate({ id: editingReceiver.id, ...values });
        setReceivers((prev) => prev.map((r) => (r.id === editingReceiver.id ? { ...r, ...values } : r)));
        if (selectedReceiverID === editingReceiver.id) onSelectReceiver?.({ ...editingReceiver, ...values });
        notify.success({ message: "Cập nhật người nhận thành công" });
      } else {
        const existing = receivers.find((r) => r.phone === values.phone);
        if (existing) {
          notify.error({ message: "Số điện thoại đã tồn tại, vui lòng nhập số khác" });
          return;
        }
        const created = await handleCreate({ sellerID, ...values });
        if (created) {
          setReceivers((prev) => [created, ...prev.filter((r) => r.id !== created.id)]);
          onSelectReceiver?.(created);
        }
        notify.success({ message: "Thêm người nhận thành công" });
      }
      setReceiverFormVisible(false);
    } catch {
      notify.error({ message: "Thao tác thất bại" });
    }
  };

  const handleDeleteReceiver = async (id) => {
    try {
      await handleDelete(id);
      if (selectedReceiverID === id) onSelectReceiver?.(null);
      await loadReceivers();
      notify.success({ message: "Xóa người nhận thành công" });
    } catch {
      notify.error({ message: "Xóa thất bại" });
    }
  };

  return (
    <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
      {/* Section: Kho nhận hàng */}
      <div style={{ borderBottom: "1px solid #E5E7EB" }}>
        <div style={{ padding: "16px 24px 12px", background: "#F8FAFC", borderBottom: "1px solid #E5E7EB" }}>
          <Row align="middle" gutter={6}>
            <Col>
              <ShopOutlined style={{ color: "#2563EB", fontSize: 15 }} />
            </Col>
            <Col>
              <Typography.Text strong style={{ fontSize: 14, color: "#111827" }}>
                Kho nhận hàng
              </Typography.Text>
            </Col>
          </Row>
        </div>

        <Spin spinning={loadingWarehouses}>
          <div style={{ padding: "12px 24px 16px", display: "flex", flexWrap: "wrap", gap: 10 }}>
            {companyOwnedWarehouses.length === 0 && !loadingWarehouses ? (
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                Không có kho phù hợp
              </Typography.Text>
            ) : (
              companyOwnedWarehouses.map((w) => {
                const isSelected = selectedWarehouseID === w.id;
                return (
                  <div
                    key={w.id}
                    onClick={() => onSelectWarehouse?.(w)}
                    style={{
                      border: `1.5px solid ${isSelected ? "#2563EB" : "#E5E7EB"}`,
                      borderRadius: 10,
                      padding: "10px 16px",
                      cursor: "pointer",
                      background: isSelected ? "#EFF6FF" : "#FFFFFF",
                      minWidth: 160,
                      position: "relative",
                      transition: "all 0.15s",
                    }}
                  >
                    {isSelected && (
                      <CheckCircleFilled
                        style={{ position: "absolute", top: 8, right: 8, color: "#2563EB", fontSize: 14 }}
                      />
                    )}
                    <Typography.Text
                      strong
                      style={{ fontSize: 13, color: isSelected ? "#1D4ED8" : "#111827", display: "block" }}
                    >
                      {`Kho ${w.name}`}
                    </Typography.Text>
                    {w.address && (
                      <Typography.Text style={{ fontSize: 11, color: "#6B7280", display: "block", marginTop: 2 }}>
                        {w.address}
                      </Typography.Text>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </Spin>
      </div>

      {/* Section: Người nhận */}
      <div>
        <div style={{ padding: "16px 24px 12px", background: "#F8FAFC", borderBottom: "1px solid #E5E7EB" }}>
          <Row align="middle" justify="space-between">
            <Row align="middle" gutter={6}>
              <Col>
                <UserOutlined style={{ color: "#2563EB", fontSize: 15 }} />
              </Col>
              <Col>
                <Typography.Text strong style={{ fontSize: 14, color: "#111827" }}>
                  Người nhận tại kho
                </Typography.Text>
              </Col>
            </Row>
            <Button
              icon={<PlusOutlined />}
              size="small"
              type="primary"
              ghost
              onClick={() => {
                setEditingReceiver(null);
                setReceiverFormVisible(true);
              }}
              style={{ borderColor: "#2563EB", color: "#2563EB" }}
            >
              Thêm mới
            </Button>
          </Row>
        </div>

        <Spin spinning={loadingReceivers || loadingDelete}>
          <div style={{ padding: "12px 24px 16px", maxHeight: 280, overflowY: "auto" }}>
            {receiversError ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <Typography.Text type="danger" style={{ fontSize: 13 }}>
                  Không thể tải danh sách người nhận. <Typography.Link onClick={loadReceivers}>Thử lại</Typography.Link>
                </Typography.Text>
              </div>
            ) : receivers.length === 0 && !loadingReceivers ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Chưa có người nhận nào. Thêm mới bên trên.
                </Typography.Text>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[...receivers]
                  .sort((a, b) => (a.id === selectedReceiverID ? -1 : b.id === selectedReceiverID ? 1 : 0))
                  .map((receiver) => {
                    const isSelected = selectedReceiverID === receiver.id;
                    return (
                      <div
                        key={receiver.id}
                        onClick={() => onSelectReceiver?.(receiver)}
                        style={{
                          border: `1.5px solid ${isSelected ? "#2563EB" : "#E5E7EB"}`,
                          borderRadius: 10,
                          padding: "10px 14px",
                          cursor: "pointer",
                          background: isSelected ? "#EFF6FF" : "#FFFFFF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          transition: "all 0.15s",
                        }}
                      >
                        <Row align="middle" gutter={10} style={{ flex: 1, minWidth: 0 }}>
                          {isSelected && (
                            <Col>
                              <CheckCircleFilled style={{ color: "#2563EB", fontSize: 16 }} />
                            </Col>
                          )}
                          <Col flex="auto" style={{ minWidth: 0 }}>
                            <Typography.Text
                              strong
                              style={{ fontSize: 13, color: isSelected ? "#1D4ED8" : "#111827", display: "block" }}
                            >
                              {receiver.fullName}
                            </Typography.Text>
                            <Typography.Text style={{ fontSize: 12, color: "#6B7280" }}>
                              {receiver.phone}
                            </Typography.Text>
                          </Col>
                        </Row>

                        <Row gutter={4} onClick={(e) => e.stopPropagation()}>
                          <Col>
                            <Button
                              icon={<EditOutlined />}
                              size="small"
                              type="text"
                              style={{ color: "#6B7280" }}
                              onClick={() => {
                                setEditingReceiver(receiver);
                                setReceiverFormVisible(true);
                              }}
                            />
                          </Col>
                          {!isSelected && (
                            <Col>
                              <Popconfirm
                                title="Xóa người nhận này?"
                                onConfirm={() => handleDeleteReceiver(receiver.id)}
                                okText="Xóa"
                                cancelText="Hủy"
                                okButtonProps={{ danger: true }}
                              >
                                <Button icon={<DeleteOutlined />} size="small" type="text" danger />
                              </Popconfirm>
                            </Col>
                          )}
                        </Row>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </Spin>
      </div>

      <ReceiverFormModal
        visible={receiverFormVisible}
        onClose={() => setReceiverFormVisible(false)}
        onSave={handleSaveReceiver}
        initialValues={editingReceiver}
        loading={loadingCreate || loadingUpdate}
      />
    </div>
  );
};

export default PickupWarehousePanel;
